import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Save, UserPlus, Upload, X, Pencil, Ban, CheckCircle2, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import BookingAutomationPanel from './BookingAutomationPanel';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Public-facing coach display profiles (separate from the user account and from
// rep_profiles). Used on funnel end pages + coach merge fields. Admin-managed.
// Coach availability (Batch 4) — merged with Google Calendar to produce bookable slots.
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];   // index 0=Mon .. 6=Sun (matches backend)
const TZ_LIST = (typeof Intl.supportedValuesOf === 'function')
    ? Intl.supportedValuesOf('timeZone')
    : ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Australia/Sydney'];
const DEFAULT_AVAIL = {
    enabled: false, timezone: (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'),
    working_days: [0, 1, 2, 3, 4], work_start: '09:00', work_end: '17:00',
    slot_duration_min: 30, buffer_min: 0, min_notice_hours: 4, max_window_days: 30,
    scarcity_enabled: false, scarcity_per_day: null, scarcity_per_week: null,
};

const EMPTY_FORM = {
    display_name: '', first_name: '', title: '', bio: '',
    phone: '', linkedin_url: '', email: '', active: true,
    paused: false, daily_cap: null, weekly_cap: null,
    static_meeting_link: '',
    photo_url: '', photo_s3_key: '', availability: { ...DEFAULT_AVAIL },
};

const inputCls = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500";
const labelCls = "block text-xs font-medium text-stone-600 mb-1";

export default function CoachesTab() {
    const { getAuthHeader } = useAuth();
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState(null);      // coach_profile_id being edited
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const photoRef = useRef(null);

    const [pickerOpen, setPickerOpen] = useState(false);
    const [availUsers, setAvailUsers] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);

    // Round-Robin settings (Batch 5)
    const [settings, setSettings] = useState(null);
    const [settingsSaving, setSettingsSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/coaches`, { headers: getAuthHeader() });
            setCoaches(Array.isArray(res.data) ? res.data : []);
        } catch { toast.error('Failed to load coaches'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    const loadSettings = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/admin/coaches/round-robin/settings`, { headers: getAuthHeader() });
            setSettings(res.data || null);
        } catch { /* non-fatal — panel just won't render */ }
    }, [getAuthHeader]);

    useEffect(() => { load(); loadSettings(); }, [load, loadSettings]);

    const saveSettings = async (patch) => {
        setSettingsSaving(true);
        try {
            const res = await axios.put(`${API}/api/admin/coaches/round-robin/settings`, patch, { headers: getAuthHeader() });
            setSettings(res.data || null);
            toast.success(patch.reset_cursor ? 'Rotation reset' : 'Round-robin settings saved');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save settings');
        } finally { setSettingsSaving(false); }
    };

    const moveCoach = async (idx, d) => {
        const j = idx + d;
        if (j < 0 || j >= coaches.length) return;
        const next = [...coaches];
        [next[idx], next[j]] = [next[j], next[idx]];
        setCoaches(next);   // optimistic
        try {
            await axios.post(`${API}/api/admin/coaches/reorder`, { ordered_ids: next.map(c => c.coach_profile_id) }, { headers: getAuthHeader() });
        } catch {
            toast.error('Failed to reorder');
            load();   // revert to server truth
        }
    };

    const openEdit = (coach) => {
        setEditing(coach.coach_profile_id);
        setForm({
            display_name: coach.display_name || '', first_name: coach.first_name || '',
            title: coach.title || '', bio: coach.bio || '', phone: coach.phone || '',
            linkedin_url: coach.linkedin_url || '', email: coach.email || '',
            active: coach.active !== false,
            paused: !!coach.paused,
            daily_cap: coach.daily_cap ?? null, weekly_cap: coach.weekly_cap ?? null,
            static_meeting_link: coach.static_meeting_link || '',
            photo_url: coach.photo_url || '', photo_s3_key: coach.photo_s3_key || '',
            availability: { ...DEFAULT_AVAIL, ...(coach.availability || {}) },
        });
        setEditOpen(true);
    };

    const setAvail = (p) => setForm(f => ({ ...f, availability: { ...(f.availability || DEFAULT_AVAIL), ...p } }));
    const toggleDay = (i) => setForm(f => {
        const cur = new Set((f.availability?.working_days) || []);
        cur.has(i) ? cur.delete(i) : cur.add(i);
        return { ...f, availability: { ...(f.availability || DEFAULT_AVAIL), working_days: [...cur].sort((a, b) => a - b) } };
    });
    const toggleScarcity = (on) => {
        setAvail({ scarcity_enabled: on });
        if (on) toast('Limiting availability can raise perceived demand, but it reduces the slots a lead can book — a deliberate trade-off.');
    };

    const handleSave = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            // Never send the blob: preview URL — only the durable s3 key.
            const { photo_url, ...payload } = form;
            await axios.put(`${API}/api/admin/coaches/${editing}`, payload, { headers: getAuthHeader() });
            toast.success('Coach profile saved');
            setEditOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save coach');
        } finally { setSaving(false); }
    };

    const handlePhotoUpload = async (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
        setUploading(true);
        try {
            const urlRes = await axios.post(
                `${API}/api/admin/coaches/${editing}/photo-upload-url`,
                { file_name: file.name, mime_type: file.type },
                { headers: getAuthHeader() },
            );
            const { upload_url, s3_key } = urlRes.data;
            await fetch(upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
            // Persist the s3 key immediately; show a local preview. The blob URL is
            // ONLY for preview — it is never sent back to the API (v2.16.34 lesson).
            await axios.put(`${API}/api/admin/coaches/${editing}`, { photo_s3_key: s3_key }, { headers: getAuthHeader() });
            const localUrl = URL.createObjectURL(file);
            setForm(f => ({ ...f, photo_url: localUrl, photo_s3_key: s3_key }));
            toast.success('Photo uploaded');
        } catch {
            toast.error('Failed to upload photo');
        } finally { setUploading(false); }
    };

    const handleDeactivate = async (coach) => {
        try {
            await axios.delete(`${API}/api/admin/coaches/${coach.coach_profile_id}`, { headers: getAuthHeader() });
            toast.success(`${coach.display_name} deactivated`);
            load();
        } catch { toast.error('Failed to deactivate'); }
    };

    const openPicker = async () => {
        setPickerOpen(true);
        setPickerLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/coaches/users-available`, { headers: getAuthHeader() });
            setAvailUsers(Array.isArray(res.data) ? res.data : []);
        } catch { toast.error('Failed to load users'); }
        finally { setPickerLoading(false); }
    };

    const createFromUser = async (u) => {
        try {
            const res = await axios.post(`${API}/api/admin/coaches`, { user_id: u.user_id }, { headers: getAuthHeader() });
            toast.success('Coach profile created');
            setPickerOpen(false);
            await load();
            openEdit(res.data);   // immediately let the admin diverge the content
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to create coach profile');
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold text-stone-800">Coaches</h3>
                    <p className="text-sm text-stone-500">Public-facing coach profiles shown to leads on funnel pages and used in coach email merge fields. Independent of the user's account settings.</p>
                </div>
                <Button onClick={openPicker}><UserPlus className="w-4 h-4 mr-1.5" /> Add Coach</Button>
            </div>

            {/* Round-Robin Settings (Batch 5) */}
            {settings && (
                <div className="mb-5 rounded-xl border border-stone-200 bg-stone-50 p-4">
                    <p className="text-sm font-semibold text-stone-800 mb-3">Round-robin assignment</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Default mode</label>
                            <select className={inputCls} value={settings.default_mode || 'sequential'}
                                disabled={settingsSaving}
                                onChange={e => saveSettings({ default_mode: e.target.value })}>
                                <option value="sequential">Sequential</option>
                                <option value="availability_based">Availability-based</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Lookahead (days)</label>
                            <input type="number" min="1" className={inputCls}
                                value={settings.lookahead_days ?? 14}
                                disabled={settingsSaving}
                                onChange={e => setSettings(s => ({ ...s, lookahead_days: e.target.value === '' ? '' : Number(e.target.value) }))}
                                onBlur={e => { const v = e.target.value === '' ? null : Number(e.target.value); if (v != null) saveSettings({ lookahead_days: v }); }} />
                            <p className="text-[11px] text-stone-400 mt-1">Used for availability-based mode.</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-200">
                        <p className="text-xs text-stone-500">
                            Next up: <span className="font-medium text-stone-700">{
                                (coaches.find(c => c.coach_profile_id === settings.sequential_cursor)?.display_name) || '—'
                            }</span>
                        </p>
                        <Button variant="outline" size="sm" disabled={settingsSaving} onClick={() => saveSettings({ reset_cursor: true })}>
                            {settingsSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />} Reset rotation
                        </Button>
                    </div>
                </div>
            )}

            {/* Post-Booking Automation (Batch 7a) */}
            <BookingAutomationPanel />

            {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
            ) : coaches.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl">
                    <p className="text-sm text-stone-500">No coach profiles yet.</p>
                    <Button variant="outline" className="mt-3" onClick={openPicker}><Plus className="w-4 h-4 mr-1.5" /> Create one from a user</Button>
                </div>
            ) : (
                <div className="space-y-2">
                    {coaches.map((c, idx) => (
                        <div key={c.coach_profile_id} className="flex items-center gap-3 py-2.5 px-3 rounded-lg border border-stone-200 bg-white">
                            <div className="flex flex-col -my-1">
                                <button onClick={() => moveCoach(idx, -1)} disabled={idx === 0} className="p-0.5 text-stone-400 hover:text-stone-700 disabled:opacity-30" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                                <button onClick={() => moveCoach(idx, 1)} disabled={idx === coaches.length - 1} className="p-0.5 text-stone-400 hover:text-stone-700 disabled:opacity-30" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                            </div>
                            {c.photo_url ? (
                                <img src={c.photo_url} alt={c.display_name} className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 font-semibold">
                                    {(c.display_name || '?').charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-stone-800 truncate">{c.display_name}</div>
                                <div className="text-xs text-stone-500 truncate">{c.title || '—'}</div>
                            </div>
                            {c.paused && (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Paused</span>
                            )}
                            {c.active !== false ? (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                            ) : (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">Inactive</span>
                            )}
                            <button onClick={() => openEdit(c)} className="p-2 text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded-lg" title="Edit">
                                <Pencil className="w-4 h-4" />
                            </button>
                            {c.active !== false && (
                                <button onClick={() => handleDeactivate(c)} className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Deactivate">
                                    <Ban className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Edit dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Coach Profile</DialogTitle>
                        <DialogDescription>Edits here never change the linked user's account settings.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Photo */}
                        <div className="flex items-center gap-4">
                            {form.photo_url ? (
                                <img src={form.photo_url} alt="" className="w-16 h-16 rounded-full object-cover" />
                            ) : (
                                <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 text-xl font-semibold">
                                    {(form.display_name || '?').charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div>
                                <input ref={photoRef} type="file" accept="image/*" className="hidden"
                                    onChange={e => { handlePhotoUpload(e.target.files[0]); e.target.value = ''; }} />
                                <Button variant="outline" size="sm" disabled={uploading} onClick={() => photoRef.current?.click()}>
                                    {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-1.5" /> Upload photo</>}
                                </Button>
                                <p className="text-xs text-stone-400 mt-1">PNG/JPG, under 5MB</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Display name</label>
                                <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>First name</label>
                                <input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className={inputCls} />
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>Title</label>
                            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="Senior Sales Coach" />
                        </div>
                        <div>
                            <label className={labelCls}>Bio</label>
                            <textarea rows={3} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} className={inputCls} placeholder="Short bio shown to leads on the booking page." />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Email (display)</label>
                                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Phone</label>
                                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={inputCls} />
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>LinkedIn URL</label>
                            <input value={form.linkedin_url} onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} className={inputCls} placeholder="https://linkedin.com/in/…" />
                        </div>
                        <div>
                            <label className={labelCls}>Static meeting link</label>
                            <input value={form.static_meeting_link} onChange={e => setForm(f => ({ ...f, static_meeting_link: e.target.value }))} className={inputCls} placeholder="https://zoom.us/j/… or https://meet.google.com/…" />
                            <p className="text-[11px] text-stone-400 mt-1">Fallback meeting URL (Zoom/Meet) used when this coach hasn't connected a calendar.</p>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded border-stone-300" />
                            Active (available for assignment)
                        </label>

                        {/* Round-robin pause + caps (Batch 5) */}
                        <div className="border-t border-stone-100 pt-4 space-y-3">
                            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                <input type="checkbox" checked={form.paused} onChange={e => setForm(f => ({ ...f, paused: e.target.checked }))} className="rounded border-stone-300" />
                                Paused (excluded from round-robin)
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Daily cap</label>
                                    <input type="number" min="1" className={inputCls} placeholder="Unlimited"
                                        value={form.daily_cap ?? ''} onChange={e => setForm(f => ({ ...f, daily_cap: e.target.value === '' ? null : Number(e.target.value) }))} />
                                </div>
                                <div>
                                    <label className={labelCls}>Weekly cap</label>
                                    <input type="number" min="1" className={inputCls} placeholder="Unlimited"
                                        value={form.weekly_cap ?? ''} onChange={e => setForm(f => ({ ...f, weekly_cap: e.target.value === '' ? null : Number(e.target.value) }))} />
                                </div>
                            </div>
                            <p className="text-[11px] text-stone-400">Caps limit how many leads round-robin assigns per day / week. Empty = unlimited.</p>
                        </div>

                        {/* Availability (Batch 4) */}
                        <div className="border-t border-stone-100 pt-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-stone-800 cursor-pointer mb-2">
                                <input type="checkbox" checked={form.availability?.enabled || false} onChange={e => setAvail({ enabled: e.target.checked })} className="rounded border-stone-300" />
                                Availability for booking
                            </label>
                            {form.availability?.enabled && (
                                <div className="space-y-3 pl-1">
                                    <div>
                                        <label className={labelCls}>Timezone</label>
                                        <select className={inputCls} value={form.availability.timezone || ''} onChange={e => setAvail({ timezone: e.target.value })}>
                                            {TZ_LIST.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>Working days</label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {DOW.map((d, i) => {
                                                const on = (form.availability.working_days || []).includes(i);
                                                return <button type="button" key={i} onClick={() => toggleDay(i)}
                                                    className={`px-2.5 py-1 rounded-md text-xs border ${on ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'}`}>{d}</button>;
                                            })}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><label className={labelCls}>Work start</label><input type="time" className={inputCls} value={form.availability.work_start || '09:00'} onChange={e => setAvail({ work_start: e.target.value })} /></div>
                                        <div><label className={labelCls}>Work end</label><input type="time" className={inputCls} value={form.availability.work_end || '17:00'} onChange={e => setAvail({ work_end: e.target.value })} /></div>
                                        <div><label className={labelCls}>Slot duration (min)</label>
                                            <select className={inputCls} value={form.availability.slot_duration_min || 30} onChange={e => setAvail({ slot_duration_min: Number(e.target.value) })}>
                                                {[15, 20, 30, 45, 60].map(n => <option key={n} value={n}>{n}</option>)}
                                            </select></div>
                                        <div><label className={labelCls}>Buffer (min)</label><input type="number" min="0" className={inputCls} value={form.availability.buffer_min ?? 0} onChange={e => setAvail({ buffer_min: Number(e.target.value) })} /></div>
                                        <div><label className={labelCls}>Min notice (hours)</label><input type="number" min="0" className={inputCls} value={form.availability.min_notice_hours ?? 4} onChange={e => setAvail({ min_notice_hours: Number(e.target.value) })} /></div>
                                        <div><label className={labelCls}>Max window (days)</label><input type="number" min="1" className={inputCls} value={form.availability.max_window_days ?? 30} onChange={e => setAvail({ max_window_days: Number(e.target.value) })} /></div>
                                    </div>
                                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                        <input type="checkbox" checked={form.availability.scarcity_enabled || false} onChange={e => toggleScarcity(e.target.checked)} className="rounded border-stone-300" />
                                        Limit visible slots (scarcity)
                                    </label>
                                    {form.availability.scarcity_enabled && (
                                        <div className="grid grid-cols-2 gap-3 pl-1">
                                            <div><label className={labelCls}>Max / day</label><input type="number" min="1" className={inputCls} value={form.availability.scarcity_per_day ?? ''} onChange={e => setAvail({ scarcity_per_day: e.target.value === '' ? null : Number(e.target.value) })} /></div>
                                            <div><label className={labelCls}>Max / week</label><input type="number" min="1" className={inputCls} value={form.availability.scarcity_per_week ?? ''} onChange={e => setAvail({ scarcity_per_week: e.target.value === '' ? null : Number(e.target.value) })} /></div>
                                        </div>
                                    )}
                                    <p className="text-xs text-stone-400">Coaches connect Google Calendar in Settings → Integrations; busy times merge automatically. (Outlook/Teams coming later.)</p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
                            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Create-from-user picker */}
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Add a coach</DialogTitle>
                        <DialogDescription>Create a coach profile from an existing team member. You can edit the public content next.</DialogDescription>
                    </DialogHeader>
                    {pickerLoading ? (
                        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
                    ) : (
                        <div className="space-y-1">
                            {availUsers.length === 0 && <p className="text-sm text-stone-500 py-6 text-center">No users found.</p>}
                            {availUsers.map(u => (
                                <button key={u.user_id} disabled={u.has_coach_profile} onClick={() => createFromUser(u)}
                                    className={`w-full flex items-center gap-3 py-2 px-3 rounded-lg text-left ${u.has_coach_profile ? 'opacity-50 cursor-not-allowed' : 'hover:bg-stone-50'}`}>
                                    <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 text-sm font-semibold">
                                        {(u.name || u.email || '?').charAt(0).toUpperCase()}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-stone-800 truncate">{u.name || u.email}</div>
                                        <div className="text-xs text-stone-500 truncate">{u.title || u.email}</div>
                                    </div>
                                    {u.has_coach_profile
                                        ? <span className="text-xs text-stone-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Added</span>
                                        : <Plus className="w-4 h-4 text-stone-400" />}
                                </button>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
